import React, { useState, useEffect } from "react";
import { Plus, Trash2, Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/context/AdminAuthContext";
import { imgSrc } from "@/lib/img";
import { Field, Input, Textarea, Select, Toggle, Btn } from "@/admin/ui";
import { ImageUpload } from "@/admin/ImageUpload";

const EMPTY = { name: "", tagline: "", description: "", price: "", discount_type: "none", discount_value: "", unit: "200g pouch", image: "", gallery: [], collection: "The Moringa Collection", category: "", badge: "", benefits: "", variants: [], ingredients: "", how_to_use: "", nutrition: [], serving: "", stock: "in_stock", active: true, sort_order: 0 };
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const toForm = (p) => (p?.id ? { ...EMPTY, ...p, description: p.description || "", price: p.price ?? "", discount_type: p.discount_type || "none", discount_value: p.discount_value || "", badge: p.badge || "", benefits: (p.benefits || []).join(", "), gallery: p.gallery || [], variants: p.variants || [], nutrition: p.nutrition || [], sort_order: p.sort_order ?? 0 } : EMPTY);

const finalPrice = (base, type, value) => {
  const b = Number(base) || 0, v = Number(value) || 0;
  if (type === "percent") return Math.max(b - (b * v) / 100, 0);
  if (type === "flat") return Math.max(b - v, 0);
  return b;
};

const Block = ({ title, sub, children }) => (
  <div className="border border-forest/10 rounded-2xl p-5 space-y-4">
    <div><h3 className="font-serif text-lg text-forest">{title}</h3>{sub && <p className="text-xs text-mutedgreen mt-0.5">{sub}</p>}</div>
    {children}
  </div>
);

const GalleryUpload = ({ value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await adminApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      onChange([...value, ...urls]);
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} added`);
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap gap-3">
      {value.map((src, i) => (
        <div key={`${src}-${i}`} className="relative w-20 h-20 rounded-xl overflow-hidden border border-forest/10">
          <img src={imgSrc(src)} alt="" className="w-full h-full object-cover" />
          <button type="button" data-testid={`gallery-remove-${i}`} onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 text-red-700"><X size={12} /></button>
        </div>
      ))}
      <label data-testid="gallery-upload-button" className="w-20 h-20 rounded-xl border-2 border-dashed border-forest/20 flex flex-col items-center justify-center text-mutedgreen hover:border-gold hover:text-gold cursor-pointer text-[10px] font-semibold">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <><Upload size={16} /> Add</>}
        <input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="gallery-upload-input" onChange={(e) => upload(Array.from(e.target.files || []))} />
      </label>
    </div>
  );
};

export const ProductForm = ({ product, onSaved, onCancel }) => {
  const [form, setForm] = useState(toForm(product));
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  useEffect(() => { adminApi.get("/admin/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);
  const setRow = (key, i, field, v) => setForm((f) => ({ ...f, [key]: f[key].map((r, j) => (j === i ? { ...r, [field]: v } : r)) }));
  const delRow = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || form.price === "") return toast.error("Name and price are required");
    if (form.variants.some((v) => !v.label || v.price === "" || v.price === undefined)) return toast.error("Every pack size needs a label and price");
    setBusy(true);
    const payload = {
      ...form,
      price: Number(form.price),
      discount_value: form.discount_type === "none" ? 0 : Number(form.discount_value) || 0,
      sort_order: Number(form.sort_order) || 0,
      badge: form.badge.trim() || null,
      benefits: form.benefits.split(",").map((b) => b.trim()).filter(Boolean),
      variants: form.variants.map((v) => ({ id: v.id || slug(v.label), label: v.label, price: Number(v.price), stock: v.stock || "in_stock" })),
      nutrition: form.nutrition.filter((r) => r.label && r.value),
    };
    delete payload.id; delete payload.mrp; delete payload.final_price; delete payload.discount_label; delete payload.price_from; delete payload.in_stock;
    try {
      const { data } = product?.id ? await adminApi.put(`/admin/products/${product.id}`, payload) : await adminApi.post("/admin/products", payload);
      toast.success(product?.id ? "Product updated" : "Product created — it's live on the website");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not save product"));
    } finally {
      setBusy(false);
    }
  };

  const preview = finalPrice(form.price, form.discount_type, form.discount_value);

  return (
    <form data-testid="product-form" onSubmit={save} className="space-y-5">
      <Block title="Basics">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Product name"><Input data-testid="product-name-input" value={form.name} onChange={onInput("name")} required /></Field>
          <Field label="Collection"><Input data-testid="product-collection-input" value={form.collection} onChange={onInput("collection")} /></Field>
        </div>
        <Field label="Shop-by-category page" hint="Controls which category landing page this product appears on.">
          <Select data-testid="product-category-select" value={form.category} onChange={onInput("category")}>
            <option value="">— Not assigned —</option>
            {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="Tagline (short line under the name)"><Input data-testid="product-tagline-input" value={form.tagline} onChange={onInput("tagline")} /></Field>
        <Field label="Description"><Textarea data-testid="product-description-input" value={form.description} onChange={onInput("description")} /></Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Default unit / pack" hint="Used when no pack sizes are added"><Input data-testid="product-unit-input" value={form.unit} onChange={onInput("unit")} placeholder="200g pouch" /></Field>
          <Field label="Badge (optional)"><Input data-testid="product-badge-input" value={form.badge} onChange={onInput("badge")} placeholder="Bestseller, New" /></Field>
          <Field label="Product availability">
            <Select data-testid="product-stock-select" value={form.stock} onChange={onInput("stock")}>
              <option value="in_stock">In stock</option>
              <option value="sold_out">Out of stock</option>
              <option value="coming_soon">Coming soon</option>
            </Select>
          </Field>
        </div>
        <Field label="Benefits (comma separated)"><Input data-testid="product-benefits-input" value={form.benefits} onChange={onInput("benefits")} placeholder="Daily Nourishment, Natural Energy" /></Field>
      </Block>

      <Block title="Pricing & discount" sub="Discount applies to the base price and to every pack size.">
        <div className="grid sm:grid-cols-4 gap-4 items-end">
          <Field label="Base price (₹)" hint="Regular / MRP"><Input data-testid="product-price-input" type="number" min="0" step="1" value={form.price} onChange={onInput("price")} required /></Field>
          <Field label="Discount type">
            <Select data-testid="product-discount-type" value={form.discount_type} onChange={onInput("discount_type")}>
              <option value="none">No discount</option>
              <option value="percent">Percentage off (%)</option>
              <option value="flat">Flat off (₹)</option>
            </Select>
          </Field>
          <Field label={form.discount_type === "percent" ? "Discount (%)" : "Discount (₹)"}><Input data-testid="product-discount-value" type="number" min="0" disabled={form.discount_type === "none"} value={form.discount_value} onChange={onInput("discount_value")} /></Field>
          <div className="bg-[#FBF6E8] border border-gold/30 rounded-xl px-4 py-3">
            <span className="block text-[10px] font-bold uppercase tracking-[0.14em] text-forest/70">Customer pays</span>
            <span data-testid="product-discount-preview" className="font-serif text-2xl text-forest">₹{preview.toFixed(0)}</span>
            {form.discount_type !== "none" && Number(form.discount_value) > 0 && <s className="text-xs text-mutedgreen ml-2">₹{form.price}</s>}
          </div>
        </div>
      </Block>

      <Block title="Pack sizes (variants)" sub="Optional. Add sizes like 250g / 500g / 1kg with their own price and stock. Customers choose one before adding to basket.">
        <div className="space-y-2">
          {form.variants.map((v, i) => (
            <div key={i} data-testid={`variant-row-${i}`} className="grid grid-cols-[1fr_110px_140px_36px] gap-2 items-center">
              <Input data-testid={`variant-label-${i}`} placeholder="Label e.g. 500g pouch" value={v.label} onChange={(e) => setRow("variants", i, "label", e.target.value)} />
              <Input data-testid={`variant-price-${i}`} type="number" min="0" placeholder="₹ price" value={v.price} onChange={(e) => setRow("variants", i, "price", e.target.value)} />
              <Select data-testid={`variant-stock-${i}`} value={v.stock || "in_stock"} onChange={(e) => setRow("variants", i, "stock", e.target.value)}>
                <option value="in_stock">In stock</option>
                <option value="sold_out">Out of stock</option>
              </Select>
              <button type="button" data-testid={`variant-remove-${i}`} onClick={() => delRow("variants", i)} className="p-2 text-mutedgreen hover:text-red-700"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        <Btn type="button" variant="ghost" data-testid="add-variant-button" onClick={() => setForm((f) => ({ ...f, variants: [...f.variants, { id: "", label: "", price: "", stock: "in_stock" }] }))}><Plus size={13} /> Add pack size</Btn>
      </Block>

      <Block title="Images">
        <ImageUpload value={form.image} onChange={set("image")} testId="product-image" />
        <div>
          <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-forest/70 mb-2">Gallery (extra photos on the product page)</span>
          <GalleryUpload value={form.gallery} onChange={set("gallery")} />
        </div>
      </Block>

      <Block title="Product details" sub="Shown on the product page.">
        <Field label="Ingredients"><Textarea data-testid="product-ingredients-input" rows={2} value={form.ingredients} onChange={onInput("ingredients")} /></Field>
        <Field label="How to use"><Textarea data-testid="product-how-to-use-input" rows={3} value={form.how_to_use} onChange={onInput("how_to_use")} /></Field>
        <Field label="Serving info"><Input data-testid="product-serving-input" value={form.serving} onChange={onInput("serving")} placeholder="1 tsp (3g) · approx. 65 servings" /></Field>
        <div>
          <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-forest/70 mb-2">Nutrition facts (per serving)</span>
          <div className="space-y-2">
            {form.nutrition.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_36px] gap-2 items-center">
                <Input data-testid={`nutrition-label-${i}`} placeholder="Energy" value={r.label} onChange={(e) => setRow("nutrition", i, "label", e.target.value)} />
                <Input data-testid={`nutrition-value-${i}`} placeholder="9 kcal" value={r.value} onChange={(e) => setRow("nutrition", i, "value", e.target.value)} />
                <button type="button" onClick={() => delRow("nutrition", i)} className="p-2 text-mutedgreen hover:text-red-700"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
          <Btn type="button" variant="ghost" className="mt-3" data-testid="add-nutrition-button" onClick={() => setForm((f) => ({ ...f, nutrition: [...f.nutrition, { label: "", value: "" }] }))}><Plus size={13} /> Add row</Btn>
        </div>
      </Block>

      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
        <div className="flex items-center gap-6">
          <Toggle testId="product-active-toggle" checked={form.active} onChange={set("active")} label="Visible on website" />
          <Field label="Order" className="w-20 !mb-0"><Input data-testid="product-order-input" type="number" value={form.sort_order} onChange={onInput("sort_order")} /></Field>
        </div>
        <div className="flex gap-2">
          <Btn type="button" variant="ghost" onClick={onCancel} data-testid="product-form-cancel">Cancel</Btn>
          <Btn type="submit" busy={busy} data-testid="product-form-save">{product?.id ? "Save changes" : "Create product"}</Btn>
        </div>
      </div>
    </form>
  );
};
